import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Ruler, Eye, Clock, CheckCircle, XCircle, Loader2, Image, Mail } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800', icon: Clock },
  reviewed: { label: 'Reviewed', color: 'bg-blue-100 text-blue-800', icon: Eye },
  quoted: { label: 'Quoted', color: 'bg-purple-100 text-purple-800', icon: Mail },
  accepted: { label: 'Accepted', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const CustomOrders = () => {
  const { toast } = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  const loadRequests = async () => {
    try {
      const { data } = await api.get('/custom-size');
      setRequests(data);
    } catch (e) {
      console.error('Failed to load custom size requests:', e);
      toast({ title: 'Error', description: 'Failed to load requests', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { loadRequests(); }, []);

  const filteredRequests = statusFilter === 'all' ? requests : requests.filter(r => r.status === statusFilter);

  const handleUpdateStatus = async () => {
    if (!newStatus || !selected) return;
    setUpdatingStatus(true);
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      formData.append('admin_notes', adminNotes);
      const token = localStorage.getItem('at1s_token');
      await fetch(`${API_BASE}/api/custom-size/${selected.id}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      toast({ title: 'Updated', description: `Status changed to ${newStatus}` });
      setSelected(null);
      setNewStatus('');
      setAdminNotes('');
      loadRequests();
    } catch (e) {
      console.error('Status update failed:', e);
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    } finally { setUpdatingStatus(false); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-5" data-testid="custom-orders-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Ruler className="h-6 w-6 text-purple-600" /> Custom Size Requests
            </h1>
            <p className="text-sm text-slate-500 mt-1">{requests.length} total requests</p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filteredRequests.length === 0 ? (
          <Card className="p-12 text-center">
            <Ruler className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400">No custom size requests yet</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map((req) => {
              const sc = statusConfig[req.status] || statusConfig.pending;
              return (
                <Card key={req.id} className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => { setSelected(req); setNewStatus(req.status); setAdminNotes(req.admin_notes || ''); }}
                  data-testid={`custom-req-${req.id}`}
                >
                  <CardContent className="py-4 px-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Ruler className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 font-mono text-sm">{req.id}</span>
                            <Badge className={`${sc.color} text-xs`}>{sc.label}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-0.5">
                            <span className="font-medium">{req.customer_name}</span> — {req.size_description || `${req.width} x ${req.height} x ${req.depth}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">{fmtDate(req.created_at)}</p>
                        {req.image_urls?.length > 0 && (
                          <span className="text-xs text-purple-500 flex items-center gap-1 justify-end mt-1"><Image className="h-3 w-3" /> {req.image_urls.length} photo{req.image_urls.length > 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={v => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-lg" data-testid="custom-order-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Ruler className="h-5 w-5 text-purple-600" /> {selected.id}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-slate-400 font-semibold uppercase">Customer</p>
                    <p className="font-medium text-slate-800">{selected.customer_name}</p>
                    <p className="text-xs text-slate-500">{selected.customer_email}</p>
                    {selected.customer_phone && <p className="text-xs text-slate-500">{selected.customer_phone}</p>}
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-semibold uppercase">Submitted</p>
                    <p className="text-sm text-slate-700">{fmtDate(selected.created_at)}</p>
                  </div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-600 font-semibold uppercase mb-1">Custom Size</p>
                  <p className="text-lg font-bold text-slate-800">{selected.size_description || `${selected.width}" × ${selected.height}" × ${selected.depth}"`}</p>
                </div>
                {selected.notes && (
                  <div>
                    <p className="text-xs text-slate-400 font-semibold uppercase">Notes</p>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded-lg p-2">{selected.notes}</p>
                  </div>
                )}
                {selected.image_urls?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-400 font-semibold uppercase mb-2">Photos</p>
                    <div className="flex flex-wrap gap-2">
                      {selected.image_urls.map((url, i) => (
                        <a key={url} href={`${API_BASE}${url}`} target="_blank" rel="noopener noreferrer" className="block">
                          <img src={`${API_BASE}${url}`} alt={`Photo ${i + 1}`} className="w-20 h-20 rounded-lg border object-cover hover:opacity-80 transition-opacity" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                <div className="border-t pt-3 space-y-3">
                  <p className="text-xs text-slate-400 font-semibold uppercase">Update Status</p>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="update-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="accepted">Accepted</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={adminNotes}
                    onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Admin notes (optional)"
                    data-testid="admin-notes-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
                <Button onClick={handleUpdateStatus} disabled={updatingStatus} className="bg-purple-600 hover:bg-purple-700" data-testid="save-status-btn">
                  {updatingStatus ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CustomOrders;
